import { Routes, Route, Navigate, useMatch } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { LookupsProvider } from './context/LookupsContext';
import { AlertsProvider } from './context/AlertsContext';
import Layout from './components/Layout';
import { Loader } from './components/Loader';
import Login from './pages/Login/Login';
import MyDay from './pages/MyDay/MyDay';
import Board from './pages/Board/Board';
import Calendar from './pages/Calendar/Calendar';
import Dashboard from './pages/Dashboard/Dashboard';
import Retainers from './pages/Retainers/Retainers';
import RetainerDetail from './pages/RetainerDetail/RetainerDetail';
import JobDetail from './pages/JobDetail/JobDetail';
import Collections from './pages/Collections/Collections';
import Clients from './pages/Clients/Clients';
import ClientCard from './pages/ClientCard/ClientCard';
import Settlements from './pages/Settlements/Settlements';
import Settings from './pages/Settings/Settings';
import Leads from './pages/Leads/Leads';
import LeadImport from './pages/LeadImport/LeadImport';
import Expenses from './pages/Expenses/Expenses';
import Documents from './pages/Documents/Documents';
import ClientDocuments from './pages/ClientDocuments/ClientDocuments';
import Notes from './pages/Notes/Notes';
import Lists from './pages/Lists/Lists';
import Users from './pages/Users/Users';
import LimitedShell from './components/LimitedShell';
import Uploads from './pages/Uploads/Uploads';
import ListPrint from './pages/ListPrint/ListPrint';
import { isPartner as isPartnerRole, isLimited } from './utils/roles';

export default function App() {
  const { partner, loading } = useAuth();
  const isPartner = isPartnerRole(partner);
  const printing = useMatch('/lists/:taskId/print');

  if (loading) return <div className="center-screen"><Loader /></div>;
  if (!partner) return <Login />;

  // ⚠ A CONTENT WRITER DOES NOT GET THE STAFF SHELL. Not a filtered version of it — a
  // different one. The staff shell is built on LookupsProvider and AlertsProvider, and
  // both fetch (/settings, /alerts) before any route renders; the server refuses both
  // for this role, so the shell would have nothing to draw and a sidebar with nothing
  // in it. See components/LimitedShell.js.
  //
  // This is a rendering decision, never a permission: every endpoint is classified on
  // the server, and a writer who types any URL is refused by the API whatever this
  // returns.
  if (isLimited(partner)) {
    return (
      <LimitedShell>
        <Routes>
          {/* ONE SCREEN, and everything else returns to it. A writer has nowhere else
              to be, and the server refuses every other endpoint regardless. */}
          <Route path="/" element={<Uploads />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </LimitedShell>
    );
  }

  // A LIST ON PAPER is a page of its own, without the sidebar, the top bar or the bell —
  // it is printed, and nothing around it may be. Staff only, behind the same login.
  if (printing) {
    return (
      <Routes>
        <Route path="/lists/:taskId/print" element={<ListPrint />} />
      </Routes>
    );
  }

  return (
    // Both providers sit inside the auth gate: neither fetches before there is a
    // session. Alerts wraps the Layout because the bell and the sidebar dots are part
    // of the shell, not of any one route.
    <LookupsProvider>
      <AlertsProvider>
        <Layout>
          <Routes>
            {/* THE LANDING PAGE IS THE DAY, not the task board. Opening the app answers
                "what does today hold" — where I have to be, what has come due — and the
                board is one of the places that answer links to. It moved to /board, and
                the two other places that had '/' written into them moved with it (see
                utils/boardLink, which is now the only copy of that route). */}
            <Route path="/" element={<MyDay />} />
            <Route path="/board" element={<Board />} />
            <Route path="/calendar" element={<Calendar />} />
            {/* The pinboard, beside the day and the diary. STAFF like both of them —
                a content writer never reaches the staff shell at all. */}
            <Route path="/notes" element={<Notes />} />
            <Route path="/lists" element={<Lists />} />
            <Route path="/leads" element={<Leads />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/retainers" element={<Retainers key="monthly" billing="monthly" />} />
            <Route path="/retainers/:id" element={<RetainerDetail />} />
            {/* The one-off projects screen (067): engagements billed one fixed amount, listed by
                the same component as the retainers. /projects/:id opens the same detail page. */}
            <Route path="/jobs" element={<Retainers key="fixed" billing="fixed" />} />
            <Route path="/projects/:id" element={<RetainerDetail />} />
            <Route path="/jobs/:id" element={<JobDetail />} />
            <Route path="/money" element={<Collections />} />
            <Route path="/expenses" element={<Expenses />} />
            <Route path="/documents" element={<Documents />} />
            <Route path="/client-documents" element={<ClientDocuments />} />
            <Route path="/clients" element={<Clients />} />
            <Route path="/clients/:id" element={<ClientCard />} />
            <Route path="/leads/import" element={<LeadImport />} />
            <Route path="/settlements" element={<Settlements />} />
            <Route path="/settings" element={<Settings />} />
            {/* Partner only. The route is hidden from everyone else so a secretary is
                not shown a screen that would 403 on every request — but the guard that
                MATTERS is the server's: /admin/users is mounted partner-only, and
                typing the URL reaches nothing. A client-side check is a courtesy, never
                a boundary. */}
            {isPartner && <Route path="/users" element={<Users />} />}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Layout>
      </AlertsProvider>
    </LookupsProvider>
  );
}
